import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit, Trash2, Save, Loader2, X, GripVertical, Video, FileText, FileQuestion, Copy, ExternalLink } from 'lucide-react';
import { getAllCourses, createCourse, updateCourse, deleteCourse } from '../firebase/courses';
import { ImageUpload } from './ImageUpload';
import { RichTextEditor } from './RichTextEditor';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FeatureGuide, HelpTooltip, InfoBanner } from './HelpTooltip';

// Sortable Module Item
function SortableModule({ module, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-white rounded-xl shadow-md border border-gray-200 p-4 mb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
          >
            <GripVertical size={18} />
          </button>
          <div className="flex-1">
            <h4 className="font-semibold text-gray-800">{module.title || 'Untitled Module'}</h4>
            <p className="text-sm text-gray-500">
              {module.lessons?.length || 0} lesson{module.lessons?.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(module)}
            className="p-2 text-gray-400 hover:text-[#c5a059] transition"
          >
            <Edit size={16} />
          </button>
          <button
            onClick={() => onDelete(module.id)}
            className="p-2 text-gray-400 hover:text-red-600 transition"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminCoursesTab({ showToast }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    setLoading(true);
    try {
      const coursesData = await getAllCourses();
      setCourses(coursesData);
    } catch (error) {
      console.error('Error loading courses:', error);
      showToast('Failed to load courses', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (courseData) => {
    try {
      let result;
      if (editingCourse) {
        result = await updateCourse(editingCourse.id, courseData);
      } else {
        result = await createCourse(courseData);
      }

      if (result.success) {
        showToast(`Course ${editingCourse ? 'updated' : 'created'} successfully`, 'success');
        setShowModal(false);
        setEditingCourse(null);
        loadCourses();
      } else {
        showToast('Error saving course: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error saving course: ' + error.message, 'error');
    }
  };

  const handleDelete = async (courseId) => {
    if (!confirm('Delete this course? This action cannot be undone.')) return;
    
    try {
      const result = await deleteCourse(courseId);
      if (result.success) {
        showToast('Course deleted successfully', 'success');
        loadCourses();
      } else {
        showToast('Error deleting course: ' + result.error, 'error');
      }
    } catch (error) {
      showToast('Error deleting course: ' + error.message, 'error');
    }
  };

  const handleDragEnd = async (courseId, event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const course = courses.find(c => c.id === courseId);
      if (!course) return;

      const oldIndex = course.modules.findIndex(m => m.id === active.id);
      const newIndex = course.modules.findIndex(m => m.id === over.id);
      const newModules = arrayMove(course.modules, oldIndex, newIndex);

      const reorderedModules = newModules.map((module, index) => ({
        ...module,
        order: index
      }));

      await handleSave({
        ...course,
        modules: reorderedModules
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="animate-spin text-[#c5a059]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold text-gray-800">Course Builder</h2>
          <HelpTooltip
            title="Course Builder"
            content="Create and manage online courses with modules and lessons. Build structured learning experiences with videos, text content, PDFs, and quizzes. Students enroll when they purchase a course product. Track their progress through modules and lessons."
          />
        </div>
        <button
          onClick={() => {
            setEditingCourse(null);
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
        >
          <Plus size={18} className="mr-2" />
          New Course
        </button>
      </div>

      <FeatureGuide
        title="Course Builder Guide"
        description="Create comprehensive online courses by organizing content into modules and lessons. Students progress through your course step-by-step. Link courses to products so customers can purchase and access them."
        steps={[
          "Click 'New Course' to create your first course",
          "Enter course title and description",
          "Upload a course thumbnail image",
          "Set status to 'Published' when ready",
          "Add modules to organize your content (e.g., 'Module 1: Introduction')",
          "Add lessons to each module (video, text, PDF, or quiz)",
          "Drag modules to reorder them",
          "Link this course to a product in the Products tab",
          "Students who purchase the product will have access to the course"
        ]}
        tips={[
          "Break content into digestible modules (3-7 modules per course)",
          "Keep lessons focused (10-20 minutes each)",
          "Use videos for demonstrations, text for concepts, PDFs for resources",
          "Add quizzes to reinforce learning",
          "Start with an introduction module to set expectations",
          "End with a conclusion module summarizing key points",
          "Test your course flow before publishing"
        ]}
      />

      <InfoBanner type="info" title="Course Access">
        Courses are accessed by students who purchase a product linked to the course. Create a product in the Products tab with type 'Online Course' and link it to your course. When customers purchase that product, they'll automatically get access to the course content.
      </InfoBanner>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map(course => (
          <div key={course.id} className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
            {course.thumbnail && (
              <img src={course.thumbnail} alt={course.title} className="w-full h-48 object-cover" />
            )}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-1">{course.title || 'Untitled Course'}</h3>
                  <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                    course.status === 'published' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {course.status || 'draft'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingCourse(course);
                      setShowModal(true);
                    }}
                    className="p-2 text-gray-400 hover:text-[#c5a059] transition"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(course.id)}
                    className="p-2 text-gray-400 hover:text-red-600 transition"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                {course.description || 'No description'}
              </p>
              <div className="text-sm text-gray-500 mb-3">
                {course.modules?.length || 0} module{(course.modules?.length || 0) !== 1 ? 's' : ''} • {' '}
                {course.modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0) || 0} lessons
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                <div className="flex-1 text-xs text-gray-500 font-mono truncate" title={course.id}>
                  ID: {course.id}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(course.id);
                    showToast('Course ID copied to clipboard!', 'success');
                  }}
                  className="p-1.5 text-gray-400 hover:text-[#c5a059] transition"
                  title="Copy Course ID"
                >
                  <Copy size={14} />
                </button>
                {course.status === 'published' && (
                  <a
                    href={`/courses/${course.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="p-1.5 text-gray-400 hover:text-[#c5a059] transition"
                    title="View Course Page"
                  >
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
        {courses.length === 0 && (
          <div className="col-span-full text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No courses yet. Create your first course to get started.</p>
            <button
              onClick={() => {
                setEditingCourse(null);
                setShowModal(true);
              }}
              className="px-6 py-2 bg-[#c5a059] text-white rounded-lg hover:bg-[#b08e4d] transition"
            >
              Create First Course
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <CourseEditorModal
          course={editingCourse}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingCourse(null);
          }}
        />
      )}
    </div>
  );
}

// Course Editor Modal
function CourseEditorModal({ course, onSave, onClose }) {
  const [title, setTitle] = useState(course?.title || '');
  const [description, setDescription] = useState(course?.description || '');
  const [thumbnail, setThumbnail] = useState(course?.thumbnail || '');
  const [status, setStatus] = useState(course?.status || 'draft');
  const [modules, setModules] = useState(course?.modules || []);
  const [editingModule, setEditingModule] = useState(null);
  const [showModuleModal, setShowModuleModal] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleSave = () => {
    if (!title) {
      alert('Please enter a course title');
      return;
    }
    onSave({
      title,
      description,
      thumbnail,
      status,
      modules
    });
  };

  const handleAddModule = () => {
    const newModule = {
      id: `module_${Date.now()}`,
      title: 'New Module',
      lessons: [],
      order: modules.length
    };
    setModules([...modules, newModule]);
    setEditingModule(newModule);
    setShowModuleModal(true);
  };

  const handleEditModule = (module) => {
    setEditingModule(module);
    setShowModuleModal(true);
  };

  const handleSaveModule = (updatedModule) => {
    setModules(modules.map(m => m.id === updatedModule.id ? updatedModule : m));
    setShowModuleModal(false);
    setEditingModule(null);
  };

  const handleDeleteModule = (moduleId) => {
    if (!confirm('Delete this module?')) return;
    setModules(modules.filter(m => m.id !== moduleId));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = modules.findIndex(m => m.id === active.id);
      const newIndex = modules.findIndex(m => m.id === over.id);
      const newModules = arrayMove(modules, oldIndex, newIndex);
      setModules(newModules.map((m, index) => ({ ...m, order: index })));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-800">
            {course ? 'Edit Course' : 'New Course'}
          </h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059] resize-none"
            />
          </div>

          <div>
            <ImageUpload
              value={thumbnail}
              onChange={setThumbnail}
              label="Course Thumbnail"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">Modules</label>
              <button
                onClick={handleAddModule}
                className="flex items-center px-3 py-1.5 bg-[#c5a059] text-white rounded-lg text-sm hover:bg-[#b08e4d] transition"
              >
                <Plus size={16} className="mr-1" />
                Add Module
              </button>
            </div>

            {modules.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                No modules yet. Add your first module to start building the course.
              </p>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={modules.map(m => m.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {modules.map((module) => (
                    <SortableModule
                      key={module.id}
                      module={module}
                      onEdit={handleEditModule}
                      onDelete={handleDeleteModule}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
            >
              Save Course
            </button>
          </div>
        </div>
      </div>

      {showModuleModal && editingModule && (
        <ModuleEditorModal
          module={editingModule}
          onSave={handleSaveModule}
          onClose={() => {
            setShowModuleModal(false);
            setEditingModule(null);
          }}
        />
      )}
    </div>
  );
}

// Module Editor Modal
function ModuleEditorModal({ module, onSave, onClose }) {
  const [title, setTitle] = useState(module?.title || '');
  const [lessons, setLessons] = useState(module?.lessons || []);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);

  const handleSave = () => {
    if (!title) {
      alert('Please enter a module title');
      return;
    }
    onSave({
      ...module,
      title,
      lessons
    });
  };

  const handleAddLesson = () => {
    const newLesson = {
      id: `lesson_${Date.now()}`,
      title: 'New Lesson',
      type: 'video',
      content: '',
      duration: 0,
      order: lessons.length
    };
    setLessons([...lessons, newLesson]);
    setEditingLesson(newLesson);
    setShowLessonModal(true);
  };

  const handleEditLesson = (lesson) => {
    setEditingLesson(lesson);
    setShowLessonModal(true);
  };

  const handleSaveLesson = (updatedLesson) => {
    setLessons(lessons.map(l => l.id === updatedLesson.id ? updatedLesson : l));
    setShowLessonModal(false);
    setEditingLesson(null);
  };

  const handleDeleteLesson = (lessonId) => {
    if (!confirm('Delete this lesson?')) return;
    setLessons(lessons.filter(l => l.id !== lessonId));
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <Video size={16} />;
      case 'quiz': return <FileQuestion size={16} />;
      default: return <FileText size={16} />;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-800">Edit Module</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Module Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">Lessons</label>
              <button
                onClick={handleAddLesson}
                className="flex items-center px-3 py-1.5 bg-[#c5a059] text-white rounded-lg text-sm hover:bg-[#b08e4d] transition"
              >
                <Plus size={16} className="mr-1" />
                Add Lesson
              </button>
            </div>

            <div className="space-y-2">
              {lessons.map((lesson, index) => (
                <div key={lesson.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400">{index + 1}.</span>
                    {getLessonIcon(lesson.type)}
                    <span className="text-sm text-gray-700">{lesson.title || 'Untitled Lesson'}</span>
                    {lesson.duration > 0 && (
                      <span className="text-xs text-gray-500">{lesson.duration} min</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEditLesson(lesson)}
                      className="p-1 text-gray-400 hover:text-[#c5a059] transition"
                    >
                      <Edit size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteLesson(lesson.id)}
                      className="p-1 text-gray-400 hover:text-red-600 transition"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
              {lessons.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">No lessons yet. Add your first lesson.</p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
            >
              Save Module
            </button>
          </div>
        </div>
      </div>

      {showLessonModal && editingLesson && (
        <LessonEditorModal
          lesson={editingLesson}
          onSave={handleSaveLesson}
          onClose={() => {
            setShowLessonModal(false);
            setEditingLesson(null);
          }}
        />
      )}
    </div>
  );
}

// Lesson Editor Modal
function LessonEditorModal({ lesson, onSave, onClose }) {
  const [title, setTitle] = useState(lesson?.title || '');
  const [type, setType] = useState(lesson?.type || 'video');
  const [content, setContent] = useState(lesson?.content || '');
  const [duration, setDuration] = useState(lesson?.duration || 0);

  const handleSave = () => {
    if (!title) {
      alert('Please enter a lesson title');
      return;
    }
    onSave({
      ...lesson,
      title,
      type,
      content,
      duration: parseInt(duration) || 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-800">Edit Lesson</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
              >
                <option value="video">Video</option>
                <option value="text">Text</option>
                <option value="pdf">PDF</option>
                <option value="quiz">Quiz</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                min="0"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {type === 'video' ? 'Video URL' : type === 'pdf' ? 'PDF URL' : 'Content'}
            </label>
            {type === 'text' ? (
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Start writing your lesson content..."
              />
            ) : (
              <input
                type="text"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#c5a059]"
                placeholder={type === 'video' ? 'YouTube URL (e.g., https://youtube.com/watch?v=...) or video URL' : type === 'pdf' ? 'PDF URL' : 'Content URL'}
              />
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-[#c5a059] hover:bg-[#b08e4d] text-white rounded-lg transition"
            >
              Save Lesson
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

